import React, { useEffect } from 'react';

import { useAppSelector } from '../../app/hooks';
import { formatUSDNoDecimals } from '../../helpers/helperFunctions';
import { H1, H2, H3, H4, P1, P2 } from '../../styles/FontStyles';
import { colors } from '../../styles/GlobalStyles';
import { Box, Button, Flexed } from '../../styles/GlobalComponents';
import {
  BuildYourTeamIcon,
  CartIconError,
  CartIconSuccess,
  ComputerIconError,
  ComputerIconSuccess,
  EarnMultipleBonusesIcon,
  FastStartIconError,
  FastStartIconSuccess,
  FastStartPeapleIcon,
  Icon,
  UserIconError,
  UserIconSuccess
} from '../../styles/Icons';
import { PackageRankMyProgressModel } from '../Ranking/Ranking.types';
import { Link, useHistory } from 'react-router-dom';
import {
  CASHBACK_BONUS,
  ENTRY_PACKAGE,
  MY_TEAM,
  SETTINGS_VIRTUAL_OFFICE
} from '../../routes/routes';
import { TextWithTooltip } from '../../components/common/TextWithTooltip';

// Lightweight inline icons so this component works without adding a new
// dependency. Swap these for your own icon set if you already use one
// (lucide-react, phosphor, react-icons, etc.).

export const JourneyToSuccess = ({
  ranking
}: {
  ranking: PackageRankMyProgressModel[];
}) => {
  const { dashboardInfo, teamBonusInfo } = useAppSelector(
    (state) => state.dashboard
  );

  const getNextRank = (ranking: PackageRankMyProgressModel[]) => {
    const nextRank = ranking?.slice(0, -3)?.find((r) => r.isNextRank);
    if (!nextRank) {
      return ranking.slice(0, -3)[0];
    }

    return nextRank;
  };

  const history = useHistory();

  const showPeapleIcons = () => {
    let content = [];
    for (let i = 0; i < 6; i++) {
      content.push(
        <Icon key={i}>
          <FastStartPeapleIcon />
        </Icon>
      );
    }

    return content;
  };

  return (
    <div>
      <H3
        center
        style={{ fontSize: 30, marginBottom: 30 }}
        color={colors.primary900}>
        My journey to success
      </H3>
      <Link to={ENTRY_PACKAGE}>
        <Flexed alignStart mb='30'>
          <Icon>
            {dashboardInfo?.user?.hasPackageOrder ? (
              <CartIconSuccess />
            ) : (
              <CartIconError />
            )}
          </Icon>
          <div>
            <H2 ml='20' mb='5' bold color={colors?.primary900}>
              1. Buy your package
            </H2>
            <P1 ml='20' color={'#364153'}>
              Mine Bitcoin, leverage advanced AI, <br />
              spend freely, and access elite education
            </P1>
          </div>
        </Flexed>
      </Link>
      <Link to={SETTINGS_VIRTUAL_OFFICE}>
        <Flexed alignStart mb='30'>
          <Icon>
            {dashboardInfo?.user?.currentVirtualOfficeLicence?.licenceId !==
            null ? (
              <ComputerIconSuccess />
            ) : (
              <ComputerIconError />
            )}
          </Icon>
          <div>
            <H2 ml='20' mb='5' bold color={colors?.primary900}>
              2. Buy Back Office Subscription
            </H2>
            <P1 ml='20' color={'#364153'}>
              Activate full platform benefits
            </P1>
          </div>
        </Flexed>
      </Link>
      <Link to={MY_TEAM}>
        <Flexed alignStart mb='30'>
          <Icon>
            {getNextRank(ranking)?.numOfDirectsWithPaidPackageInLeftLeg > 0 &&
            getNextRank(ranking)?.numOfDirectsWithPaidPackageInRightLeg > 0 &&
            getNextRank(ranking)?.numOfDirectsWithPaidPackageInLeftLeg +
              getNextRank(ranking)?.numOfDirectsWithPaidPackageInRightLeg >
              2 ? (
              <UserIconSuccess />
            ) : (
              <UserIconError />
            )}
          </Icon>
          <div>
            <H2 ml='20' mb='5' bold color={colors?.primary900}>
              3. Achieve Qualified Partner status
            </H2>
            <P1 ml='20' color={'#364153'}>
              Sell 1 package on Left and 1 on Right
            </P1>
          </div>
        </Flexed>
      </Link>

      <Flexed between center mb='30' mr='50'>
        <Link to={CASHBACK_BONUS}>
          <Flexed>
            <Icon>
              {dashboardInfo?.user?.fastStartBonus?.isClaimed ? (
                <FastStartIconSuccess />
              ) : (
                <FastStartIconError />
              )}
            </Icon>
            <div>
              <H2 ml='20' mb='5' bold color={colors?.primary900}>
                4. Get Fast Start Bonus
              </H2>
              <Flexed>
                <P1 ml='20' mr='20' color={'#364153'}>
                  Enroll 5 people in 30 days
                </P1>
                {showPeapleIcons()}
              </Flexed>
            </div>
          </Flexed>
        </Link>
        <Button
          maxWidth='150px'
          style={{ height: '40px' }}
          disabled={!dashboardInfo?.user?.fastStartBonus?.isClaimed}
          onClick={() => history.push(CASHBACK_BONUS)}>
          Claim Reward
        </Button>
      </Flexed>

      <div
        style={{
          width: '100%',
          height: 1,
          backgroundColor: colors.primary900,
          margin: '40px 0px'
        }}></div>

      <Link to={MY_TEAM}>
        <Flexed alignStart mb='30'>
          <Icon>
            <BuildYourTeamIcon />
          </Icon>
          <div>
            <H2 ml='20' mb='5' bold color={'#6A7282'}>
              5. Build your Team
            </H2>
            <P1 ml='20' color={'#364153'}>
              Expand your network strategically and start your own business.
            </P1>
          </div>
        </Flexed>
      </Link>
      <Link to={MY_TEAM}>
        <Flexed alignStart mb='30'>
          <Icon>
            <EarnMultipleBonusesIcon />
          </Icon>
          <div>
            <H2 ml='20' mb='5' bold color={'#6A7282'}>
              6. Earn Multiple Bonuses
            </H2>
            <P1 ml='20' color={'#364153'}>
              Unlock your full earning potential. Claim your USDT/USDC rewards{' '}
              <br /> as your network grows.
            </P1>
            <Flexed>
              <Flexed alignStart column>
                <P1 ml='20' mb='5' mt='30' bold caps color={'#6A7282'}>
                  POWER LEG BV
                </P1>
                <P1 ml='20' color={colors?.primary900}>
                  {formatUSDNoDecimals(teamBonusInfo?.powerLegBv)}
                </P1>
              </Flexed>
              <Flexed alignStart column>
                <Flexed center>
                  <P1 ml='20' mb='5' mt='30' bold caps color={'#6A7282'}>
                    Unrealized team bonus
                  </P1>
                  <TextWithTooltip
                    margin='25px 0 0 0'
                    width='auto'
                    tooltip={
                      'Generate matching volume in your weaker leg to get this bonus.'
                    }></TextWithTooltip>
                </Flexed>
                <P1 ml='20' color={colors?.primary900}>
                  {formatUSDNoDecimals(teamBonusInfo?.unrealizedTeamBonus)}{' '}
                  USDT/USDC
                </P1>
              </Flexed>
            </Flexed>
          </div>
        </Flexed>
      </Link>
    </div>
  );
};

export default JourneyToSuccess;
